import { DateRange, UTC } from 'rewire-common';
import { observable } from 'rewire-core';
import { Modal, Form, IFormContext, validator, IError, ErrorSeverity, field, arraySearch } from 'rewire-ui';
import { capitalCase } from 'change-case';
import deepequals from 'fast-deep-equal';
import {
  ITimecardSummary,
  INameEntity,
  IWorkflow,
  FieldFilter,
  ScheduleFilterInput,
  BatchInsertInput,
  INameLike,
} from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import { IRouteManagerState, batchRouteManager, payRouteManager } from '../main-navigation/RouteManager';
import { notifications } from '../ui/Notifications';
import { getNameSearcher } from '../utility/Searchers';
import batchDetailsModel from './BatchDetailsModel';
import DashboardElementModel from './DashboardElementModel';
import DataQueries from '../graphql/Queries';

export interface IBatchModelState {
  statuses: DashboardElementModel[];
  notInBatch: DashboardElementModel;
  inBatch: DashboardElementModel;
  inError: DashboardElementModel;
  totalEmployees: DashboardElementModel;
  timecardsToAdd: number;
  locationWorkflow?: IWorkflow;
}

export interface IBatchModel extends Modal, IBatchModelState {
  localState: IBatchModelState;
  form: Form;
  counterExplanation: string;
  submit: () => Promise<boolean>;
  detailsTitle: string;
  load: (prevState: IRouteManagerState) => Promise<void>;
}

const weekDaySearcher = arraySearch(
  ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  (item: any) => (item && !Array.isArray(item) && item) || '',
);

class CreateBatchModel extends Modal implements IBatchModel {
  localState!: IBatchModelState;
  form: Form;
  detailsTitle: string = 'Batch Details';

  constructor() {
    super('Create Batch');

    const groupFilter = () =>
      this.form.field.areas.value?.length
        ? ({ field: 'areaId', in: this.form.field.areas.value.map((area: INameLike) => area.id) } as FieldFilter)
        : undefined;
    const areaSearcher = getNameSearcher('areas');
    const groupSearcher = getNameSearcher('groups', groupFilter);

    this.form = Form.create((form: IFormContext) => ({
      start: form
        .date()
        .label('Batch Start')
        .onValueChange(this.loadData)
        .validators('required', validator(this.validateEffective, field('end'))),
      end: form
        .date()
        .label('Batch End')
        .onValueChange(this.loadData)
        .validators('required', validator(this.validateEffective, field('start'))),
      areas: form
        .multiselectautocomplete(areaSearcher)
        .label('Areas')
        .placeholder('select areas')
        .onValueChange(this.updateTimecardsToAdd),
      groups: form
        .multiselectautocomplete(groupSearcher)
        .label('Groups')
        .placeholder('select groups')
        .onValueChange(this.updateTimecardsToAdd),
      startOfWeeks: form
        .multiselectautocomplete(weekDaySearcher)
        .label('Week Starting')
        .placeholder('select start of work week')
        .onValueChange(this.updateTimecardsToAdd),
    }));
    this.action('save', this.submit, {
      variant: 'contained',
      type: 'submit',
      disabled: () =>
        !this.timecardsToAdd ||
        !batchDetailsModel.form.hasChanges ||
        batchDetailsModel.form.hasErrors ||
        this.isDisabled ||
        !batchDetailsModel.canAdd,
    });

    requestAnimationFrame(() => {
      this.localState = observable({
        statuses: [],
        totalEmployees: new DashboardElementModel('Employees', 0, 0, true, '#'),
        notInBatch: new DashboardElementModel('Not in a batch', 0, 0, false, '#', '#faa000'),
        inBatch: new DashboardElementModel('In a batch', 0, 0, false, '#', '#669639'),
        inError: new DashboardElementModel('In error', 0, 0, false, '#', '#cc3300'),
        timecardsToAdd: 0,
      });
    });
  }

  loadLocationWorkflow = async () => {
    if (!this.localState.locationWorkflow) {
      this.localState.locationWorkflow = await DataQueries.getLocationWorkflow();
    }
  };

  validateEffective = (): IError | undefined => {
    const start = this.form.field.start.value;
    const end = this.form.field.end.value;

    if (!start) return { text: 'Batch Start must be specified', severity: ErrorSeverity.Error };
    if (!end) return { text: 'Batch End must be specified', severity: ErrorSeverity.Error };

    const effective: DateRange = new DateRange(start, end);
    if (!effective.isValid) return { text: 'Invalid effective range', severity: ErrorSeverity.Error };

    if (effective.length > 31) return { text: 'Batch length is limited to 31 days max', severity: ErrorSeverity.Error };

    return undefined;
  };

  public get totalEmployees(): DashboardElementModel {
    return this.localState.totalEmployees;
  }

  public get notInBatch(): DashboardElementModel {
    return this.localState.notInBatch;
  }

  public get inBatch(): DashboardElementModel {
    return this.localState.inBatch;
  }

  public get inError(): DashboardElementModel {
    return this.localState.inError;
  }

  public get statuses(): DashboardElementModel[] {
    return this.localState.statuses;
  }

  public get timecardsToAdd(): number {
    return this.localState.timecardsToAdd;
  }

  public set timecardsToAdd(value: number) {
    this.localState.timecardsToAdd = value;
  }

  get isEffectiveRangeValid() {
    const effective: DateRange = this.effectiveRange;
    return effective.isValid && effective.length <= 31;
  }

  get counterExplanation(): string {
    if (!this.isEffectiveRangeValid) return 'Invalid effective range; unable to create batch.';
    return this.timecardsToAdd === 0
      ? `There are NO timecards that: exist in the range ${this.effectiveRange.toString()}, belong to the area(s) and group(s) specified, are in the ${this.localState.locationWorkflow?.finalState ? capitalCase(this.localState.locationWorkflow.finalState) : '«unknown»'} state, are not in Error, and are not already in a batch.`
      : `Number of timecards that: exist in the range ${this.effectiveRange.toString()}, belong to the area(s) and group(s) specified, are in the ${this.localState.locationWorkflow?.finalState ? capitalCase(this.localState.locationWorkflow.finalState) : '«unknown»'} state, are not in Error, and are not already in a batch. If you click CREATE BATCH now, these timecards will be added to the new batch automatically.`;
  }

  private updateTimecardsToAdd = async () => {
    if (!this.isEffectiveRangeValid) {
      this.timecardsToAdd = 0;
      return;
    }

    const effective: DateRange = this.effectiveRange;
    const areaIds: string[] = this.form.field.areas.value?.map((v: any) => v.id);
    const groupIds: string[] = this.form.field.groups.value?.map((v: any) => v.id);
    const startOfWeeks: string[] = this.form.field.startOfWeeks.value;
    const result: number = (await DataQueries.getTimecardsNotInBatch(effective, areaIds, groupIds, startOfWeeks)) ?? 0;
    this.timecardsToAdd = result;
  };

  setDashboardElement(
    element: DashboardElementModel,
    enabled: boolean = false,
    value: number = 0,
    maxValue: number = 0,
    uri: string = '',
  ) {
    element.value = value;
    element.maxValue = maxValue;
    element.enabled = enabled;
    element.uri = uri;
  }

  private get effectiveRange(): DateRange {
    const start: UTC = this.form.field.start.value;
    const end: UTC = this.form.field.end.value;

    const effective: DateRange = new DateRange(start, end);
    return effective;
  }

  public async updateDashboard() {
    if (this.isEffectiveRangeValid) {
      const effective: DateRange = this.effectiveRange;
      const summary: ITimecardSummary = await DataQueries.getTimecardSummaryDashboard(effective);
      this.setDashboardElement(this.localState.totalEmployees, true, summary?.totalEmployees);
      this.setDashboardElement(
        this.localState.notInBatch,
        true,
        summary?.notInBatch,
        summary?.total,
        payRouteManager.createLinkFor({ effective, selectedEmployeeId: undefined, filters: { inBatch: false, onlyPayable: true } }),
      );
      this.setDashboardElement(
        this.localState.inBatch,
        true,
        summary?.inBatch,
        summary?.total,
        payRouteManager.createLinkFor({ effective, selectedEmployeeId: undefined, filters: { inBatch: true, onlyPayable: true } }),
      );
      this.setDashboardElement(
        this.localState.inError,
        true,
        summary?.errors,
        summary?.total,
        payRouteManager.createLinkFor({
          effective,
          selectedEmployeeId: undefined,
          filters: { messageLevels: ['error'], onlyPayable: true },
        }),
      );
      this.localState.statuses.set(
        summary?.statuses?.map((s) =>
          effective.isValid
            ? new DashboardElementModel(
                s.status,
                s.total,
                summary.total,
                true,
                payRouteManager.createLinkFor({
                  effective,
                  selectedEmployeeId: undefined,
                  filters: { timecardStatus: [s.status], onlyPayable: true },
                }),
              )
            : new DashboardElementModel(s.status, s.total, summary.total, false, '#'),
        ) ?? [],
      );
    } else {
      this.setDashboardElement(this.localState.totalEmployees, false);
      this.setDashboardElement(this.localState.notInBatch, false);
      this.setDashboardElement(this.localState.inBatch, false);
      this.setDashboardElement(this.localState.inError, false);
      this.localState.statuses.set([]);
    }
  }

  private shouldLoad(prevState: IRouteManagerState, route: string | undefined, filters: ScheduleFilterInput | undefined): boolean {
    if (prevState.route !== route) return true;
    if (!deepequals(prevState.filters, filters)) return true;
    return false;
  }

  load = async (prevState: IRouteManagerState) => {
    const { route, filters } = batchRouteManager;
    if (this.shouldLoad(prevState, route, filters)) {
      if (!this.form.field.start.value) {
        const { effective } = batchRouteManager;
        this.form.field.start.value = effective?.start;
        this.form.field.end.value = effective?.end;
      }
      await this.loadData();
      batchDetailsModel.load(undefined, false);
    }
  };

  loadData = async () => {
    await this.loadLocationWorkflow();
    this.updateDashboard();
    this.updateTimecardsToAdd();
  };

  private save = async (): Promise<boolean> => {
    const batchInput: any = batchDetailsModel.mapToSave();
    const input: BatchInsertInput = {
      name: batchDetailsModel.form.field.name.value,
      code: batchDetailsModel.form.field.code.value,
      effective: this.effectiveRange,
      areaIds: this.form.field.areas.value?.map((i: INameEntity) => i.id),
      groupIds: this.form.field.groups.value?.map((i: INameEntity) => i.id),
      startOfWeeks: this.form.field.startOfWeeks.value,
      tags: batchDetailsModel.form.field.tags.value,
      fields: batchInput?.fields,
    };

    const result = await DataMutations.insertBatch('', input);
    if (result?.[0]) {
      notifications.success(
        `Added ${this.localState.timecardsToAdd} timecards to newly created batch: ${batchDetailsModel.form.field.name.value}`,
      );
      return false; // return false so form dosesn't close
    }
    notifications.error('Batch Creation Failed');
    return false;
  };

  submit = async () => {
    return await this.form.submitAsync(this.save);
  };
}

const createBatchModel = observable(new CreateBatchModel());
export default createBatchModel;
